import { configureStore, combineReducers } from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from "redux-persist";
import storage from "redux-persist/lib/storage";
import MarketsSlice from "src/store/features/markets.slice";
import MarketsToolsSlice from "src/store/features/marketTool.slice";
import TreeMapFilterSlice from "src/store/features/MarketTreeMapFilter.slice";
import api from "src/store/features/api";
import TransactionsSlice from "src/store/features/transactions.slice";

//RTK Query
import { transactionsDataApi } from "src/store/services/transactionsData";
import HeaderSlice from "src/store/features/header.slice";
import SettingsSlice from "src/store/features/settings.slice";
import PriceAlertSlice from "./features/PriceAlerts.slice";
import depositModalSlice from "./features/depositModal.slice";
import TargetOrderSlice from "./features/TargetOrders.slice";
import UserSlice from "./features/user.slice";
import AddressTestingSlice from "./features/addressTesting.slice";
import ManagementSlice from "./features/AccountManagment.slice";
import OnboardingSlice from "./features/onboarding.slice";
import MaintenanceSlice from "./features/maintenance.slice";
import GlobalModalsSlice from "./features/modals.slice";
import brokerToCustomerDialog from "./features/BrokerToCustomerDialog.slice";
import DcaSlice from "./features/dca.slice";
import WithdrawalSlice from "./features/withdrawal.slice";
import { BeneficiarySlice } from "./features/beneficiary.slice";
import { TradeSlice } from "./features/trade.slice";
import { SwapSlice } from "./features/swap.slice";
import SubscribeSlice from "./features/subscribe.slice";
import RPAnalticsSlice from "./features/RPAnalytics.slice";
import ContactChangeSlice from "./features/contactChange.slice";
import { TargetOrderSliceV2 } from "./features/TargetOrdersV2.slice";
import AccountLockSlice from "src/components/settings/security/AccountLock/accountLock.slice";
import { SignUpFormSlice } from "./features/signUpForm.slice";
import { SignUpFormInputSlice } from "./features/signUpFormInput.slice";
import { TurnstileSlice } from "./features/turnstile.slice";
import { LoginFormInputSlice } from "./features/loginFormInput.slice";
import StakingSlice from "./features/staking.slice";
import TaxCenterDraftsSlice from "./features/taxCenterDrafts.slice";

const storagePersistConfig = {
  key: "root",
  version: 1,
  storage,
  // Slices in local storage
  whitelist: [MarketsSlice.name, SettingsSlice.name, SubscribeSlice.name],
};

// Nested persist for the withdrawal slice — survive page refreshes / tab
// closes when a Notabene Travel Rule transfer is mid-flight, so the user
// can come back and pick up at the "Waiting for verification…" or
// "Continue to Review" state without losing their context. Only fields
// needed to resume the flow are whitelisted; transient/security state
// (modal open, email/2FA codes, errors) is intentionally excluded.
const withdrawalPersistConfig = {
  key: "withdrawal",
  version: 1,
  storage,
  whitelist: [
    "cryptoWithdrawalStage",
    "selectedToken",
    "walletAddress",
    "displayWalletAddress",
    "memo",
    "withdrawalAmount",
    "notabeneTxId",
    "notabeneStatus",
    "notabenePolicies",
    "notabeneRequiresPii",
    "notabenePiiSubmitted",
    "withdrawalPreparedToken",
    "withdrawalPreparedExpiresAt",
  ],
};

// The Redux store consists of multiple slices combined together
// A slice is a collection of reducer logic and actions for a single feature
export const rootReducer = combineReducers({
  [api.reducerPath]: api.reducer,
  [MaintenanceSlice.name]: MaintenanceSlice.reducer,
  [ManagementSlice.name]: ManagementSlice.reducer,
  [MarketsSlice.name]: MarketsSlice.reducer,
  [AddressTestingSlice.name]: AddressTestingSlice.reducer,
  [TransactionsSlice.name]: TransactionsSlice.reducer,
  [transactionsDataApi.reducerPath]: transactionsDataApi.reducer,
  [MarketsToolsSlice.name]: MarketsToolsSlice.reducer,
  [PriceAlertSlice.name]: PriceAlertSlice.reducer,
  [TreeMapFilterSlice.name]: TreeMapFilterSlice.reducer,
  [HeaderSlice.name]: HeaderSlice.reducer,
  [TradeSlice.name]: TradeSlice.reducer,
  [SwapSlice.name]: SwapSlice.reducer,
  [TargetOrderSlice.name]: TargetOrderSlice.reducer,
  [TargetOrderSliceV2.name]: TargetOrderSliceV2.reducer,
  [depositModalSlice.name]: depositModalSlice.reducer,
  [SettingsSlice.name]: SettingsSlice.reducer,
  [GlobalModalsSlice.name]: GlobalModalsSlice.reducer,
  [UserSlice.name]: UserSlice.reducer,
  [BeneficiarySlice.name]: BeneficiarySlice.reducer,
  [OnboardingSlice.name]: OnboardingSlice.reducer,
  [DcaSlice.name]: DcaSlice.reducer,
  [WithdrawalSlice.name]: persistReducer(withdrawalPersistConfig, WithdrawalSlice.reducer),
  [brokerToCustomerDialog.name]: brokerToCustomerDialog.reducer,
  [SubscribeSlice.name]: SubscribeSlice.reducer,
  [ContactChangeSlice.name]: ContactChangeSlice.reducer,
  [RPAnalticsSlice.name]: RPAnalticsSlice.reducer,
  [AccountLockSlice.name]: AccountLockSlice.reducer,
  [SignUpFormSlice.name]: SignUpFormSlice.reducer,
  [SignUpFormInputSlice.name]: SignUpFormInputSlice.reducer,
  [TurnstileSlice.name]: TurnstileSlice.reducer,
  [LoginFormInputSlice.name]: LoginFormInputSlice.reducer,
  [StakingSlice.name]: StakingSlice.reducer,
  [TaxCenterDraftsSlice.name]: TaxCenterDraftsSlice.reducer,
});

const persistedReducer = persistReducer(storagePersistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaults) =>
    getDefaults({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        warnAfter: 128,
      },
    }).concat(transactionsDataApi.middleware, api.middleware),
});

export default store;

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
